import React, { useState, useEffect } from 'react'
import { getAccessToken } from '../utils/tokenStorage'
import {
  Box,
  Card,
  CardContent,
  Typography,
  Tabs,
  Tab,
  Button,
  TextField,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  FormHelperText,
  Grid,
  CircularProgress
} from '@mui/material'
import {
  Settings as SettingsIcon,
  Apps as AppsIcon,
  ExpandMore as ExpandMoreIcon,
  Security as SecurityIcon,
  Storage as StorageIcon,
  Email as EmailIcon,
  Save as SaveIcon,
  Share as ShareIcon,
  Assessment as AssessmentIcon,
  Visibility as VisibilityIcon,
  Assignment as TaskIcon,
  GridView as GridViewIcon
} from '@mui/icons-material'
import TaskManagementTab from '../components/admin/TaskManagementTab'
import HangingProtocolSettings from '../components/admin/HangingProtocolSettings'
import { toast } from 'react-hot-toast'
import { useAppSettings, ApplicationSettings } from '../contexts/AppSettingsContext'
import { PageHeader } from '../components/ui'


interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ px: 3, py: 1 }}>
          {children}
        </Box>
      )}
    </div>
  )
}

interface AdminSettingsPageProps {
  initialTab?: number
  hideHeader?: boolean
}

const AdminSettingsPage: React.FC<AdminSettingsPageProps> = ({ initialTab = 0, hideHeader = false }) => {
  const [tabValue, setTabValue] = useState(initialTab)


  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue)
  }


  // Tipuri pentru setările sistemului
  interface SystemSettings {
    security: {
      access_token_expire_minutes: number
      refresh_token_expire_hours: number
      session_timeout_minutes: number
      max_login_attempts: number
      min_password_length: number
      password_min_length: number
      require_2fa: boolean
      password_require_digits: boolean
      password_require_lowercase: boolean
      password_require_uppercase: boolean
      password_require_special: boolean
      mfa_max_attempts: number
      mfa_lockout_minutes: number
      ip_whitelist: string[] | null
    }
    retention: {
      audit_log_retention_days: number
      patient_data_retention_years: number
      study_data_retention_years: number
      report_retention_years: number
      auto_cleanup_enabled: boolean
    }
    share_defaults: {
      default_share_duration_hours: number
      max_share_duration_hours: number
      require_password: boolean
      allow_download: boolean
      track_access: boolean
    }
    scoring_weights: {
      urgency_weight: number
      complexity_weight: number
      workload_weight: number
      experience_weight: number
      availability_weight: number
    }
    viewer: {
      ohif_base_url: string
      default_viewer: string
      enable_3d: boolean
      enable_measurements: boolean
    }
    smtp: {
      enabled: boolean
      mode: string
      host: string | null
      port: number
      use_tls: boolean
      use_ssl: boolean
      username: string | null
      password: string | null
      from_email: string | null
      from_name: string | null
      timeout: number
      max_retries: number
    }
    sms: {
      enabled: boolean
      connection_id: string | null
      password: string | null
    }
  }

  // Componenta pentru administrarea MFA (pentru secțiunea din SystemSettingsTab)
  const MFAAdminSection: React.FC = () => {
    const [mfaSettings, setMfaSettings] = useState<any>(null)
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)

    useEffect(() => {
      loadMfaSettings()
    }, [])

    const loadMfaSettings = async () => {
      try {
        setLoading(true)
        const token = getAccessToken()
        const response = await fetch('/api/v1/mfa/admin/settings', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })
        const data = await response.json()
        setMfaSettings(data)
      } catch (error) {
        console.error('Error loading MFA settings:', error)
        toast.error('Eroare la încărcarea setărilor MFA')
      } finally {
        setLoading(false)
      }
    }

    const saveMfaSettings = async () => {
      try {
        setSaving(true)
        const token = getAccessToken()
        await fetch('/api/v1/mfa/admin/settings', {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(mfaSettings)
        })
        toast.success('Setări MFA salvate cu succes!')
      } catch (error) {
        console.error('Error saving MFA settings:', error)
        toast.error('Eroare la salvarea setărilor MFA')
      } finally {
        setSaving(false)
      }
    }

    if (loading) return <Box sx={{ p: 2, textAlign: 'center' }}><CircularProgress /></Box>

    if (!mfaSettings) return <Alert severity="error">Nu s-au putut încărca setările MFA</Alert>

    return (
      <Box>
        <Alert severity="info" sx={{ mb: 3 }}>
          Configurați politica MFA pentru organizația dumneavoastră. Utilizatorii pot activa MFA individual din pagina lor de profil.
        </Alert>

        <Grid container spacing={3}>
          {/* Politică MFA */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>Politică MFA</Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Status MFA</InputLabel>
              <Select
                value={mfaSettings.mfa_status || 'disabled'}
                onChange={(e) => setMfaSettings({ ...mfaSettings, mfa_status: e.target.value })}
                label="Status MFA"
              >
                <MenuItem value="disabled">Dezactivat (utilizatorii nu pot activa MFA)</MenuItem>
                <MenuItem value="optional">Opțional (utilizatorii pot alege să activeze MFA)</MenuItem>
                <MenuItem value="required">Obligatoriu (toți utilizatorii trebuie să aibă MFA)</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Metode permise */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ mb: 1, mt: 2 }}>Metode MFA permise</Typography>
          </Grid>

          <Grid item xs={12} md={4}>
            <FormControlLabel
              control={
                <Switch
                  checked={mfaSettings.totp_enabled || false}
                  onChange={(e) => setMfaSettings({ ...mfaSettings, totp_enabled: e.target.checked })}
                  disabled={mfaSettings.mfa_status === 'disabled'}
                />
              }
              label="Aplicație Autentificare (TOTP)"
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <FormControlLabel
              control={
                <Switch
                  checked={mfaSettings.sms_enabled || false}
                  onChange={(e) => setMfaSettings({ ...mfaSettings, sms_enabled: e.target.checked })}
                  disabled={mfaSettings.mfa_status === 'disabled'}
                />
              }
              label="SMS"
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <FormControlLabel
              control={
                <Switch
                  checked={mfaSettings.email_enabled || false}
                  onChange={(e) => setMfaSettings({ ...mfaSettings, email_enabled: e.target.checked })}
                  disabled={mfaSettings.mfa_status === 'disabled'}
                />
              }
              label="Email"
            />
          </Grid>

          {/* Setări securitate */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2, mb: 2 }}>Setări Securitate</Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              type="number"
              label="Expirare cod (minute)"
              value={mfaSettings.code_expiry_minutes || 5}
              onChange={(e) => setMfaSettings({ ...mfaSettings, code_expiry_minutes: parseInt(e.target.value) })}
              helperText="Timpul până la expirarea codurilor SMS/Email"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              type="number"
              label="Încercări maxime"
              value={mfaSettings.max_attempts || 3}
              onChange={(e) => setMfaSettings({ ...mfaSettings, max_attempts: parseInt(e.target.value) })}
              helperText="Numărul de încercări înainte de blocare"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              type="number"
              label="Durată blocare (minute)"
              value={mfaSettings.lockout_duration_minutes || 15}
              onChange={(e) => setMfaSettings({ ...mfaSettings, lockout_duration_minutes: parseInt(e.target.value) })}
              helperText="Timpul de blocare după prea multe încercări"
            />
          </Grid>

          {/* Backup codes */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2, mb: 2 }}>Coduri de Rezervă</Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={mfaSettings.backup_codes_enabled || false}
                  onChange={(e) => setMfaSettings({ ...mfaSettings, backup_codes_enabled: e.target.checked })}
                />
              }
              label="Activează coduri de rezervă"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              type="number"
              label="Număr coduri de rezervă"
              value={mfaSettings.backup_codes_count || 10}
              onChange={(e) => setMfaSettings({ ...mfaSettings, backup_codes_count: parseInt(e.target.value) })}
              disabled={!mfaSettings.backup_codes_enabled}
            />
          </Grid>

          {/* Trusted Devices */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2, mb: 2 }}>Dispozitive de Încredere</Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={mfaSettings.trusted_devices_enabled || false}
                  onChange={(e) => setMfaSettings({ ...mfaSettings, trusted_devices_enabled: e.target.checked })}
                />
              }
              label='Permite "Ține minte acest dispozitiv"'
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              type="number"
              label="Valabilitate dispozitiv (zile)"
              value={mfaSettings.trusted_device_expiry_days || 7}
              onChange={(e) => setMfaSettings({ ...mfaSettings, trusted_device_expiry_days: parseInt(e.target.value) })}
              disabled={!mfaSettings.trusted_devices_enabled}
              helperText="Numărul de zile până la expirarea încrederii"
            />
          </Grid>

          {/* Save Button */}
          <Grid item xs={12}>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={saveMfaSettings}
              disabled={saving}
              sx={{ mt: 2 }}
            >
              {saving ? <CircularProgress size={24} /> : 'Salvează Setări MFA'}
            </Button>
          </Grid>
        </Grid>
      </Box>
    )
  }

  // Componenta pentru setările sistemului
  const SystemSettingsTab: React.FC = () => {
    const [settings, setSettings] = useState<SystemSettings | null>(null)
    const [loading, setLoading] = useState(false)
    const [saving, setSaving] = useState(false)

    useEffect(() => {
      fetchSettings()
    }, [])

    const fetchSettings = async () => {
      try {
        setLoading(true)
        const response = await fetch('/api/v1/admin/settings', {
          headers: {
            'Authorization': `Bearer ${getAccessToken()}`
          }
        })
        
        if (response.ok) {
          const data = await response.json()
          setSettings(data)
        } else {
          toast.error('Eroare la încărcarea setărilor')
        }
      } catch (error) {
        toast.error('Eroare la încărcarea setărilor')
      } finally {
        setLoading(false)
      }
    }

    const handleSaveSettings = async (updatedSettings: Partial<SystemSettings>) => {
      try {
        setSaving(true)
        const response = await fetch('/api/v1/admin/settings', {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${getAccessToken()}`
          },
          body: JSON.stringify(updatedSettings)
        })

        if (response.ok) {
          toast.success('Setările au fost salvate cu succes')
          fetchSettings()
        } else {
          const error = await response.json()
          toast.error(error.detail || 'Eroare la salvarea setărilor')
        }
      } catch (error) {
        toast.error('Eroare la salvarea setărilor')
      } finally {
        setSaving(false)
      }
    }

    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <Typography>Se încarcă setările...</Typography>
        </Box>
      )
    }

    if (!settings) {
      return (
        <Alert severity="error">
          Nu s-au putut încărca setările sistemului.
        </Alert>
      )
    }

    // Compact styles for inputs
    const inputSx = { '& .MuiInputBase-root': { fontSize: '0.8rem' }, '& .MuiInputLabel-root': { fontSize: '0.75rem' }, '& .MuiFormHelperText-root': { fontSize: '0.68rem' } };
    const accordionSx = { '& .MuiAccordionSummary-root': { minHeight: 40, '&.Mui-expanded': { minHeight: 40 } }, '& .MuiAccordionSummary-content': { my: 0.5 } };

    return (
      <Box>
        <Box sx={{ mb: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography sx={{ fontSize: '0.95rem', fontWeight: 600 }}>Configurări Sistem</Typography>
          <Button variant="contained" startIcon={<SaveIcon />} onClick={() => handleSaveSettings(settings)} disabled={saving} size="small" sx={{ height: 28, fontSize: '0.75rem', textTransform: 'none' }}>{saving ? 'Se salvează...' : 'Salvează'}</Button>
        </Box>

        {/* Securitate */}
        <Accordion defaultExpanded sx={accordionSx}>
          <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: 18 }} />}>
            <SecurityIcon sx={{ mr: 1, fontSize: 18 }} />
            <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Securitate</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ py: 1 }}>
            <Grid container spacing={1.5}>
              {/* Token Settings */}
              <Grid item xs={12} md={6}>
                <TextField label="Access Token Expire (minute)" type="number" value={settings.security.access_token_expire_minutes || 15} onChange={(e) => setSettings(prev => prev ? { ...prev, security: { ...prev.security, access_token_expire_minutes: parseInt(e.target.value) || 15 } } : null)} fullWidth size="small" sx={inputSx} helperText="Timp expirare access token" />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField label="Refresh Token Expire (ore)" type="number" value={settings.security.refresh_token_expire_hours || 12} onChange={(e) => setSettings(prev => prev ? { ...prev, security: { ...prev.security, refresh_token_expire_hours: parseInt(e.target.value) || 12 } } : null)} fullWidth size="small" sx={inputSx} helperText="Timp expirare refresh token" />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField label="Timeout Sesiune (minute)" type="number" value={settings.security.session_timeout_minutes || 360} onChange={(e) => setSettings(prev => prev ? { ...prev, security: { ...prev.security, session_timeout_minutes: parseInt(e.target.value) || 360 } } : null)} fullWidth size="small" sx={inputSx} helperText="Durata sesiunii" />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField label="Încercări Maxime Login" type="number" value={settings.security.max_login_attempts || 6} onChange={(e) => setSettings(prev => prev ? { ...prev, security: { ...prev.security, max_login_attempts: parseInt(e.target.value) || 6 } } : null)} fullWidth size="small" sx={inputSx} helperText="Nr maxim încercări login" />
              </Grid>
              {/* Password Settings */}
              <Grid item xs={12} md={6}>
                <TextField label="Lungime Minimă Parolă" type="number" value={settings.security.min_password_length || 8} onChange={(e) => setSettings(prev => prev ? { ...prev, security: { ...prev.security, min_password_length: parseInt(e.target.value) || 8 } } : null)} fullWidth size="small" sx={inputSx} helperText="Lungime minimă" />
              </Grid>
              <Grid item xs={12} md={6}>
                <Alert severity="info" sx={{ mt: 1 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>MFA este acum configurat în tab-ul dedicat!</strong>
                  </Typography>
                  <Typography variant="caption">
                    Mergeți la tab-ul "MFA & Securitate" pentru configurarea completă a politicii MFA pentru organizație.
                  </Typography>
                </Alert>
              </Grid>

              {/* Password Requirements */}
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.security.password_require_digits || true}
                      onChange={(e) => setSettings(prev => prev ? {
                        ...prev,
                        security: {
                          ...prev.security,
                          password_require_digits: e.target.checked
                        }
                      } : null)}
                    />
                  }
                  label="Parolă trebuie să conțină cifre"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.security.password_require_lowercase || true}
                      onChange={(e) => setSettings(prev => prev ? {
                        ...prev,
                        security: {
                          ...prev.security,
                          password_require_lowercase: e.target.checked
                        }
                      } : null)}
                    />
                  }
                  label="Parolă trebuie să conțină litere mici"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.security.password_require_uppercase || true}
                      onChange={(e) => setSettings(prev => prev ? {
                        ...prev,
                        security: {
                          ...prev.security,
                          password_require_uppercase: e.target.checked
                        }
                      } : null)}
                    />
                  }
                  label="Parolă trebuie să conțină litere mari"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.security.password_require_special || false}
                      onChange={(e) => setSettings(prev => prev ? {
                        ...prev,
                        security: {
                          ...prev.security,
                          password_require_special: e.target.checked
                        }
                      } : null)}
                    />
                  }
                  label="Parolă trebuie să conțină caractere speciale"
                />
              </Grid>

              {/* MFA Settings */}
              <Grid item xs={12} md={6}>
                <TextField
                  label="Încercări Maxime MFA"
                  type="number"
                  value={settings.security.mfa_max_attempts || 3}
                  onChange={(e) => setSettings(prev => prev ? {
                    ...prev,
                    security: {
                      ...prev.security,
                      mfa_max_attempts: parseInt(e.target.value) || 3
                    }
                  } : null)}
                  fullWidth
                  helperText="Numărul maxim de încercări MFA"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="MFA Lockout (minute)"
                  type="number"
                  value={settings.security.mfa_lockout_minutes || 30}
                  onChange={(e) => setSettings(prev => prev ? {
                    ...prev,
                    security: {
                      ...prev.security,
                      mfa_lockout_minutes: parseInt(e.target.value) || 30
                    }
                  } : null)}
                  fullWidth
                  helperText="Timpul de blocare după eșecuri MFA"
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Retenție */}
        <Accordion sx={accordionSx}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <StorageIcon sx={{ mr: 2 }} />
            <Typography variant="h6">Retenție Date</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ py: 1 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.retention.auto_cleanup_enabled || true}
                      onChange={(e) => setSettings(prev => prev ? {
                        ...prev,
                        retention: {
                          ...prev.retention,
                          auto_cleanup_enabled: e.target.checked
                        }
                      } : null)}
                    />
                  }
                  label="Curățare Automată Activă"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Retenție Log-uri Audit (zile)"
                  type="number"
                  value={settings.retention.audit_log_retention_days || 2555}
                  onChange={(e) => setSettings(prev => prev ? {
                    ...prev,
                    retention: {
                      ...prev.retention,
                      audit_log_retention_days: parseInt(e.target.value) || 2555
                    }
                  } : null)}
                  fullWidth
                  helperText="Numărul de zile pentru păstrarea log-urilor de audit"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Retenție Date Pacienți (ani)"
                  type="number"
                  value={settings.retention.patient_data_retention_years || 10}
                  onChange={(e) => setSettings(prev => prev ? {
                    ...prev,
                    retention: {
                      ...prev.retention,
                      patient_data_retention_years: parseInt(e.target.value) || 10
                    }
                  } : null)}
                  fullWidth
                  helperText="Numărul de ani pentru păstrarea datelor pacienților"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Retenție Date Studii (ani)"
                  type="number"
                  value={settings.retention.study_data_retention_years || 10}
                  onChange={(e) => setSettings(prev => prev ? {
                    ...prev,
                    retention: {
                      ...prev.retention,
                      study_data_retention_years: parseInt(e.target.value) || 10
                    }
                  } : null)}
                  fullWidth
                  helperText="Numărul de ani pentru păstrarea datelor studiilor"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Retenție Rapoarte (ani)"
                  type="number"
                  value={settings.retention.report_retention_years || 10}
                  onChange={(e) => setSettings(prev => prev ? {
                    ...prev,
                    retention: {
                      ...prev.retention,
                      report_retention_years: parseInt(e.target.value) || 10
                    }
                  } : null)}
                  fullWidth
                  helperText="Numărul de ani pentru păstrarea rapoartelor"
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Share Defaults */}
        <Accordion sx={accordionSx}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <ShareIcon sx={{ mr: 2 }} />
            <Typography variant="h6">Setări Partajare</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ py: 1 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Durata Default Partajare (ore)"
                  type="number"
                  value={settings.share_defaults.default_share_duration_hours || 24}
                  onChange={(e) => setSettings(prev => prev ? {
                    ...prev,
                    share_defaults: {
                      ...prev.share_defaults,
                      default_share_duration_hours: parseInt(e.target.value) || 24
                    }
                  } : null)}
                  fullWidth
                  helperText="Durata implicită pentru partajări (ore)"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Durata Maximă Partajare (ore)"
                  type="number"
                  value={settings.share_defaults.max_share_duration_hours || 168}
                  onChange={(e) => setSettings(prev => prev ? {
                    ...prev,
                    share_defaults: {
                      ...prev.share_defaults,
                      max_share_duration_hours: parseInt(e.target.value) || 168
                    }
                  } : null)}
                  fullWidth
                  helperText="Durata maximă permisă pentru partajări (ore)"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.share_defaults.require_password || true}
                      onChange={(e) => setSettings(prev => prev ? {
                        ...prev,
                        share_defaults: {
                          ...prev.share_defaults,
                          require_password: e.target.checked
                        }
                      } : null)}
                    />
                  }
                  label="Necesită Parolă"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.share_defaults.allow_download || true}
                      onChange={(e) => setSettings(prev => prev ? {
                        ...prev,
                        share_defaults: {
                          ...prev.share_defaults,
                          allow_download: e.target.checked
                        }
                      } : null)}
                    />
                  }
                  label="Permite Descărcarea"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.share_defaults.track_access || true}
                      onChange={(e) => setSettings(prev => prev ? {
                        ...prev,
                        share_defaults: {
                          ...prev.share_defaults,
                          track_access: e.target.checked
                        }
                      } : null)}
                    />
                  }
                  label="Urmărește Accesul"
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Scoring Weights */}
        <Accordion sx={accordionSx}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <AssessmentIcon sx={{ mr: 2 }} />
            <Typography variant="h6">Ponderi Scoring</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ py: 1 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Pondere Urgență"
                  type="number"
                  inputProps={{ step: 0.1, min: 0, max: 10 }}
                  value={settings.scoring_weights.urgency_weight || 1.5}
                  onChange={(e) => setSettings(prev => prev ? {
                    ...prev,
                    scoring_weights: {
                      ...prev.scoring_weights,
                      urgency_weight: parseFloat(e.target.value) || 1.5
                    }
                  } : null)}
                  fullWidth
                  helperText="Ponderea pentru urgența studiilor"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Pondere Complexitate"
                  type="number"
                  inputProps={{ step: 0.1, min: 0, max: 10 }}
                  value={settings.scoring_weights.complexity_weight || 1.2}
                  onChange={(e) => setSettings(prev => prev ? {
                    ...prev,
                    scoring_weights: {
                      ...prev.scoring_weights,
                      complexity_weight: parseFloat(e.target.value) || 1.2
                    }
                  } : null)}
                  fullWidth
                  helperText="Ponderea pentru complexitatea studiilor"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Pondere Sarcini"
                  type="number"
                  inputProps={{ step: 0.1, min: 0, max: 10 }}
                  value={settings.scoring_weights.workload_weight || 1.0}
                  onChange={(e) => setSettings(prev => prev ? {
                    ...prev,
                    scoring_weights: {
                      ...prev.scoring_weights,
                      workload_weight: parseFloat(e.target.value) || 1.0
                    }
                  } : null)}
                  fullWidth
                  helperText="Ponderea pentru volumul de sarcină"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Pondere Experiență"
                  type="number"
                  inputProps={{ step: 0.1, min: 0, max: 10 }}
                  value={settings.scoring_weights.experience_weight || 0.8}
                  onChange={(e) => setSettings(prev => prev ? {
                    ...prev,
                    scoring_weights: {
                      ...prev.scoring_weights,
                      experience_weight: parseFloat(e.target.value) || 0.8
                    }
                  } : null)}
                  fullWidth
                  helperText="Ponderea pentru experiența medicului"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Pondere Disponibilitate"
                  type="number"
                  inputProps={{ step: 0.1, min: 0, max: 10 }}
                  value={settings.scoring_weights.availability_weight || 1.1}
                  onChange={(e) => setSettings(prev => prev ? {
                    ...prev,
                    scoring_weights: {
                      ...prev.scoring_weights,
                      availability_weight: parseFloat(e.target.value) || 1.1
                    }
                  } : null)}
                  fullWidth
                  helperText="Ponderea pentru disponibilitatea medicului"
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Viewer Settings */}
        <Accordion sx={accordionSx}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <VisibilityIcon sx={{ mr: 2 }} />
            <Typography variant="h6">Configurare Viewer</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ py: 1 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Viewer Default</InputLabel>
                  <Select
                    value={settings.viewer.default_viewer || 'ohif'}
                    onChange={(e) => setSettings(prev => prev ? {
                      ...prev,
                      viewer: {
                        ...prev.viewer,
                        default_viewer: e.target.value
                      }
                    } : null)}
                    label="Viewer Default"
                  >
                    <MenuItem value="ohif">OHIF</MenuItem>
                    <MenuItem value="stone">Stone Web Viewer</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="URL Base OHIF"
                  value={settings.viewer.ohif_base_url || 'http://localhost:3000'}
                  onChange={(e) => setSettings(prev => prev ? {
                    ...prev,
                    viewer: {
                      ...prev.viewer,
                      ohif_base_url: e.target.value
                    }
                  } : null)}
                  fullWidth
                  helperText="URL-ul de bază pentru OHIF viewer"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.viewer.enable_3d || true}
                      onChange={(e) => setSettings(prev => prev ? {
                        ...prev,
                        viewer: {
                          ...prev.viewer,
                          enable_3d: e.target.checked
                        }
                      } : null)}
                    />
                  }
                  label="Activează 3D"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.viewer.enable_measurements || true}
                      onChange={(e) => setSettings(prev => prev ? {
                        ...prev,
                        viewer: {
                          ...prev.viewer,
                          enable_measurements: e.target.checked
                        }
                      } : null)}
                    />
                  }
                  label="Activează Măsurători"
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* SMTP */}
        <Accordion sx={accordionSx}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <EmailIcon sx={{ mr: 2 }} />
            <Typography variant="h6">Configurare Email (SMTP)</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ py: 1 }}>
            <Grid container spacing={3}>
              {/* Activare SMTP */}
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.smtp.enabled}
                      onChange={(e) => setSettings(prev => prev ? {
                        ...prev,
                        smtp: {
                          ...prev.smtp,
                          enabled: e.target.checked
                        }
                      } : null)}
                    />
                  }
                  label="SMTP Activ"
                />
              </Grid>

              {/* Tip SMTP */}
              <Grid item xs={12} md={6}>
                <FormControl fullWidth disabled={!settings.smtp.enabled}>
                  <InputLabel>Tip SMTP</InputLabel>
                  <Select
                    value={settings.smtp.mode || 'authenticated'}
                    onChange={(e) => setSettings(prev => prev ? {
                      ...prev,
                      smtp: {
                        ...prev.smtp,
                        mode: e.target.value
                      }
                    } : null)}
                    label="Tip SMTP"
                  >
                    <MenuItem value="authenticated">Autentificat (User/Pass)</MenuItem>
                    <MenuItem value="relay">Relay (Fără autentificare)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Host și Port */}
              <Grid item xs={12} md={6}>
                <TextField
                  label="Host SMTP"
                  value={settings.smtp.host || ''}
                  onChange={(e) => setSettings(prev => prev ? {
                    ...prev,
                    smtp: {
                      ...prev.smtp,
                      host: e.target.value
                    }
                  } : null)}
                  fullWidth
                  required
                  disabled={!settings.smtp.enabled}
                  helperText="Adresa serverului SMTP"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  label="Port SMTP"
                  type="number"
                  value={settings.smtp.port || 587}
                  onChange={(e) => setSettings(prev => prev ? {
                    ...prev,
                    smtp: {
                      ...prev.smtp,
                      port: parseInt(e.target.value) || 587
                    }
                  } : null)}
                  fullWidth
                  required
                  disabled={!settings.smtp.enabled}
                  helperText="Portul serverului SMTP (25, 465, 587, etc.)"
                  inputProps={{ min: 1, max: 65535 }}
                />
              </Grid>

              {/* SSL/TLS */}
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.smtp.use_tls || false}
                      onChange={(e) => setSettings(prev => prev ? {
                        ...prev,
                        smtp: {
                          ...prev.smtp,
                          use_tls: e.target.checked
                        }
                      } : null)}
                      disabled={!settings.smtp.enabled}
                    />
                  }
                  label="TLS Activ"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.smtp.use_ssl || false}
                      onChange={(e) => setSettings(prev => prev ? {
                        ...prev,
                        smtp: {
                          ...prev.smtp,
                          use_ssl: e.target.checked
                        }
                      } : null)}
                      disabled={!settings.smtp.enabled}
                    />
                  }
                  label="SSL Activ"
                />
              </Grid>

              {/* Credențiale (doar pentru modul autentificat) */}
              {settings.smtp.mode === 'authenticated' && (
                <>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Username SMTP"
                      value={settings.smtp.username || ''}
                      onChange={(e) => setSettings(prev => prev ? {
                        ...prev,
                        smtp: {
                          ...prev.smtp,
                          username: e.target.value
                        }
                      } : null)}
                      fullWidth
                      required
                      disabled={!settings.smtp.enabled}
                      helperText="Username-ul pentru autentificarea SMTP"
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Password SMTP"
                      type="password"
                      value={settings.smtp.password || ''}
                      onChange={(e) => setSettings(prev => prev ? {
                        ...prev,
                        smtp: {
                          ...prev.smtp,
                          password: e.target.value
                        }
                      } : null)}
                      fullWidth
                      required
                      disabled={!settings.smtp.enabled}
                      helperText="Password-ul pentru autentificarea SMTP"
                    />
                  </Grid>
                </>
              )}

              {/* Email expeditor */}
              <Grid item xs={12} md={6}>
                <TextField
                  label="Email Expeditor"
                  type="email"
                  value={settings.smtp.from_email || ''}
                  onChange={(e) => setSettings(prev => prev ? {
                    ...prev,
                    smtp: {
                      ...prev.smtp,
                      from_email: e.target.value
                    }
                  } : null)}
                  fullWidth
                  disabled={!settings.smtp.enabled}
                  helperText="Email-ul de la care se trimit mesajele"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  label="Nume Expeditor"
                  value={settings.smtp.from_name || ''}
                  onChange={(e) => setSettings(prev => prev ? {
                    ...prev,
                    smtp: {
                      ...prev.smtp,
                      from_name: e.target.value
                    }
                  } : null)}
                  fullWidth
                  disabled={!settings.smtp.enabled}
                  helperText="Numele de la care se trimit mesajele"
                />
              </Grid>

              {/* Timeout și Retry */}
              <Grid item xs={12} md={6}>
                <TextField
                  label="Timeout (secunde)"
                  type="number"
                  value={settings.smtp.timeout || 30}
                  onChange={(e) => setSettings(prev => prev ? {
                    ...prev,
                    smtp: {
                      ...prev.smtp,
                      timeout: parseInt(e.target.value) || 30
                    }
                  } : null)}
                  fullWidth
                  disabled={!settings.smtp.enabled}
                  helperText="Timeout-ul pentru conexiunea SMTP"
                  inputProps={{ min: 5, max: 300 }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  label="Încercări Maxime"
                  type="number"
                  value={settings.smtp.max_retries || 3}
                  onChange={(e) => setSettings(prev => prev ? {
                    ...prev,
                    smtp: {
                      ...prev.smtp,
                      max_retries: parseInt(e.target.value) || 3
                    }
                  } : null)}
                  fullWidth
                  disabled={!settings.smtp.enabled}
                  helperText="Numărul maxim de încercări de trimitere"
                  inputProps={{ min: 1, max: 10 }}
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* SMS */}
        <Accordion sx={accordionSx}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <EmailIcon sx={{ mr: 2 }} />
            <Typography variant="h6">Configurare SMS</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ py: 1 }}>
            <Grid container spacing={3}>
              {/* Activare SMS */}
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings?.sms?.enabled || false}
                      onChange={(e) => setSettings(prev => prev ? {
                        ...prev,
                        sms: {
                          ...prev.sms,
                          enabled: e.target.checked
                        }
                      } : null)}
                    />
                  }
                  label="Activează serviciul SMS"
                />
              </Grid>

              {/* Connection ID */}
              <Grid item xs={12} md={6}>
                <TextField
                  label="Connection ID"
                  value={settings?.sms?.connection_id || ''}
                  onChange={(e) => setSettings(prev => prev ? {
                    ...prev,
                    sms: {
                      ...prev.sms,
                      connection_id: e.target.value
                    }
                  } : null)}
                  fullWidth
                  disabled={!settings?.sms?.enabled}
                  helperText="Connection ID pentru furnizorul SMS"
                />
              </Grid>

              {/* Password */}
              <Grid item xs={12} md={6}>
                <TextField
                  label="Password"
                  type="password"
                  value={settings?.sms?.password || ''}
                  onChange={(e) => setSettings(prev => prev ? {
                    ...prev,
                    sms: {
                      ...prev.sms,
                      password: e.target.value
                    }
                  } : null)}
                  fullWidth
                  disabled={!settings?.sms?.enabled}
                  helperText="Parola pentru furnizorul SMS"
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* MFA & Securitate */}
        <Accordion sx={accordionSx}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <SecurityIcon sx={{ mr: 2 }} />
            <Typography variant="h6">MFA & Securitate</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ py: 1 }}>
            <MFAAdminSection />
          </AccordionDetails>
        </Accordion>
      </Box>
    )
  }

  // Componenta pentru setările aplicației
  const ApplicationSettingsTab: React.FC = () => {
    const { settings: originalSettings, loading, error, refreshSettings } = useAppSettings()
    const [localSettings, setLocalSettings] = useState<ApplicationSettings | null>(null)
    const [saving, setSaving] = useState(false)
    const [hasChanges, setHasChanges] = useState(false)

    // Inițializează setările locale când se încarcă setările originale
    useEffect(() => {
      if (originalSettings) {
        setLocalSettings(originalSettings)
        setHasChanges(false)
      }
    }, [originalSettings])

    const handleSaveSettings = async () => {
      if (!localSettings) return
      
      try {
        setSaving(true)
        
        // Exclude app_version from payload - it's auto-managed via CI/CD
        const { app_version, ...settingsToSave } = localSettings
        
        // Salvează în backend
        const response = await fetch('/api/v1/admin/settings', {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${getAccessToken()}`
          },
          body: JSON.stringify({ application: settingsToSave })
        })

        if (response.ok) {
          toast.success('Setările aplicației au fost salvate cu succes')
          // Reîncarcă setările din context
          await refreshSettings()
          setHasChanges(false)
        } else {
          const error = await response.json()
          toast.error(error.detail || 'Eroare la salvarea setărilor aplicației')
        }
      } catch (error) {
        toast.error('Eroare la salvarea setărilor aplicației')
      } finally {
        setSaving(false)
      }
    }

    const handleSettingChange = (key: string, value: string) => {
      if (!localSettings) return
      
      const updatedSettings = { ...localSettings, [key]: value }
      setLocalSettings(updatedSettings)
      
      // Verifică dacă există modificări față de setările originale
      if (originalSettings) {
        const hasModifications = Object.keys(updatedSettings).some(
          k => updatedSettings[k as keyof ApplicationSettings] !== originalSettings[k as keyof ApplicationSettings]
        )
        setHasChanges(hasModifications)
      }
    }

    const handleResetChanges = () => {
      if (originalSettings) {
        setLocalSettings(originalSettings)
        setHasChanges(false)
      }
    }

    // Compact styles for accordions
    const accordionSx = { '& .MuiAccordionSummary-root': { minHeight: 40, '&.Mui-expanded': { minHeight: 40 } }, '& .MuiAccordionSummary-content': { my: 0.5 } };

    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <Typography>Se încarcă setările...</Typography>
        </Box>
      )
    }

    if (error) {
      return (
        <Box sx={{ px: 3, py: 1 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      )
    }

    if (!localSettings) {
      return (
        <Box sx={{ px: 3, py: 1 }}>
          <Alert severity="warning">Nu s-au putut încărca setările aplicației</Alert>
        </Box>
      )
    }

    return (
      <Box>
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            Configurări Aplicație
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            {hasChanges && (
              <Button
                variant="outlined"
                onClick={handleResetChanges}
                disabled={saving}
              >
                Resetează Modificările
              </Button>
            )}
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSaveSettings}
              disabled={saving || !hasChanges}
            >
              {saving ? 'Se salvează...' : 'Salvează Setările Aplicației'}
            </Button>
          </Box>
        </Box>

        <Grid container spacing={3}>
          {/* Informații de bază */}
          <Grid item xs={12}>
            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6">Informații de Bază</Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ py: 1 }}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Numele Aplicației"
                      value={localSettings.app_name}
                      onChange={(e) => handleSettingChange('app_name', e.target.value)}
                      fullWidth
                      required
                      helperText="Numele principal al aplicației (ex: SynPris)"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Descrierea Aplicației"
                      value={localSettings.app_description}
                      onChange={(e) => handleSettingChange('app_description', e.target.value)}
                      fullWidth
                      required
                      helperText="Descrierea scurtă a aplicației"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontSize: '0.75rem' }}>
                        Versiunea Aplicației
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {localSettings.app_version || '-'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', fontStyle: 'italic' }}>
                        Versiunea este auto-incrementată la deploy prin CI/CD. Nu poate fi modificată manual.
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Compania"
                      value={localSettings.app_company}
                      onChange={(e) => handleSettingChange('app_company', e.target.value)}
                      fullWidth
                      required
                      helperText="Numele companiei dezvoltatoare"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Website"
                      value={localSettings.app_website}
                      onChange={(e) => handleSettingChange('app_website', e.target.value)}
                      fullWidth
                      helperText="URL-ul website-ului companiei"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Email Suport"
                      value={localSettings.app_support_email}
                      onChange={(e) => handleSettingChange('app_support_email', e.target.value)}
                      fullWidth
                      helperText="Email-ul pentru suport tehnic"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Domeniu Principal"
                      value={localSettings.primary_domain || ''}
                      onChange={(e) => handleSettingChange('primary_domain', e.target.value)}
                      fullWidth
                      placeholder="app.zenris.ro"
                      helperText="Domeniul principal al aplicației (pentru SSL). După modificare, rulați ssl-generate.sh pe server."
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Mod SSL</InputLabel>
                      <Select
                        value={localSettings.ssl_mode || 'staging'}
                        onChange={(e) => handleSettingChange('ssl_mode', e.target.value)}
                        label="Mod SSL"
                      >
                        <MenuItem value="staging">Staging (Test)</MenuItem>
                        <MenuItem value="production">Production (Real)</MenuItem>
                      </Select>
                      <FormHelperText>
                        Staging: certificate de test (netrusted). Production: certificate reale Let's Encrypt.
                      </FormHelperText>
                    </FormControl>
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
          </Grid>

          {/* Aspect și Temă */}
          <Grid item xs={12}>
            <Accordion sx={accordionSx}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6">Aspect și Temă</Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ py: 1 }}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="URL Logo"
                      value={localSettings.app_logo_url}
                      onChange={(e) => handleSettingChange('app_logo_url', e.target.value)}
                      fullWidth
                      helperText="URL-ul pentru logo-ul aplicației"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="URL Favicon"
                      value={localSettings.app_favicon_url}
                      onChange={(e) => handleSettingChange('app_favicon_url', e.target.value)}
                      fullWidth
                      helperText="URL-ul pentru favicon-ul aplicației"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Culoarea Principală"
                      value={localSettings.app_theme_primary_color}
                      onChange={(e) => handleSettingChange('app_theme_primary_color', e.target.value)}
                      fullWidth
                      type="color"
                      helperText="Culoarea principală a temei"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Culoarea Secundară"
                      value={localSettings.app_theme_secondary_color}
                      onChange={(e) => handleSettingChange('app_theme_secondary_color', e.target.value)}
                      fullWidth
                      type="color"
                      helperText="Culoarea secundară a temei"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Modul Temă</InputLabel>
                      <Select
                        value={localSettings.app_theme_mode}
                        onChange={(e) => handleSettingChange('app_theme_mode', e.target.value)}
                        label="Modul Temă"
                      >
                        <MenuItem value="light">Luminos</MenuItem>
                        <MenuItem value="dark">Întunecat</MenuItem>
                        <MenuItem value="auto">Automat</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
          </Grid>

          {/* Hanging Protocol Viewer */}
          <Grid item xs={12}>
            <Accordion sx={accordionSx}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <GridViewIcon color="primary" />
                  <Typography variant="h6">Hanging Protocol Viewer</Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ py: 1 }}>
                <HangingProtocolSettings />
              </AccordionDetails>
            </Accordion>
          </Grid>

          {/* Localizare și Format */}
          <Grid item xs={12}>
            <Accordion sx={accordionSx}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6">Localizare și Format</Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ py: 1 }}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Limba</InputLabel>
                      <Select
                        value={localSettings.app_language}
                        onChange={(e) => handleSettingChange('app_language', e.target.value)}
                        label="Limba"
                      >
                        <MenuItem value="ro">Română</MenuItem>
                        <MenuItem value="en">English</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Fusul Orar"
                      value={localSettings.app_timezone}
                      onChange={(e) => handleSettingChange('app_timezone', e.target.value)}
                      fullWidth
                      helperText="Fusul orar al aplicației"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Formatul Datei"
                      value={localSettings.app_date_format}
                      onChange={(e) => handleSettingChange('app_date_format', e.target.value)}
                      fullWidth
                      helperText="Formatul pentru afișarea datelor"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Formatul Orei"
                      value={localSettings.app_time_format}
                      onChange={(e) => handleSettingChange('app_time_format', e.target.value)}
                      fullWidth
                      helperText="Formatul pentru afișarea orei"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Moneda"
                      value={localSettings.app_currency}
                      onChange={(e) => handleSettingChange('app_currency', e.target.value)}
                      fullWidth
                      helperText="Codul monedei (ex: RON)"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Simbolul Monedei"
                      value={localSettings.app_currency_symbol}
                      onChange={(e) => handleSettingChange('app_currency_symbol', e.target.value)}
                      fullWidth
                      helperText="Simbolul monedei (ex: Lei)"
                    />
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
          </Grid>
        </Grid>
      </Box>
    )
  }

  // Dacă hideHeader este true, afișăm doar conținutul tab-ului specific
  if (hideHeader) {
    return (
      <Box>
        {tabValue === 0 && <SystemSettingsTab />}
        {tabValue === 1 && <ApplicationSettingsTab />}
        {tabValue === 2 && <TaskManagementTab />}
      </Box>
    )
  }

  return (
    <Box>
      <PageHeader
        title="Setări Admin"
      />
      
      <Box sx={{ px: 3, py: 1 }}>
      
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={tabValue} onChange={handleTabChange} aria-label="admin settings tabs">
              <Tab 
                icon={<SettingsIcon />} 
                label="Configurări Sistem" 
                iconPosition="start"
              />
              <Tab 
                icon={<AppsIcon />} 
                label="Configurări Aplicație" 
                iconPosition="start"
              />
              <Tab 
                icon={<TaskIcon />} 
                label="Task Management" 
                iconPosition="start"
              />
            </Tabs>
          </Box>

          {/* Tab Configurări Sistem */}
          <TabPanel value={tabValue} index={0}>
            <SystemSettingsTab />
          </TabPanel>

          {/* Tab Configurări Aplicație */}
          <TabPanel value={tabValue} index={1}>
            <ApplicationSettingsTab />
          </TabPanel>

          {/* Tab Task Management */}
          <TabPanel value={tabValue} index={2}>
            <TaskManagementTab />
          </TabPanel>
        </CardContent>
      </Card>
      </Box>
    </Box>
  )
}

export default AdminSettingsPage
